import React, { Component } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Table, TableHead, TableBody, TableRow, TableCell,
    Typography, Box, LinearProgress, Alert, IconButton, Chip
} from '@mui/material';
import { CloudUploadRounded, CheckCircle, Warning, Close, DeleteForever, GetAppRounded } from '@mui/icons-material';
import Dropzone from 'react-dropzone';

const VALID_PAYMENT_MODES = ["ESPECES", "CHEQUE", "PRELEVEMENT", "VIREMENT", "GAB", "MOBILE MONEY"];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

class ImportIncomesCSV extends Component {
    state = {
        phase: "PARSE", // PARSE | ATTACH | SUBMIT | REFRESHING | DONE | ERROR
        parsedRows: [],
        errors: [],
        submitProgress: 0,
        submitTotal: 0,
        submitError: null,
        createdIds: [],
        rollingBack: false,
    }

    // ---- Name-to-ID resolvers ----

    resolvePost = (name) => {
        const posts = this.props.POSTS || {};
        const found = Object.values(posts).find(p => p.post_name?.toLowerCase().trim() === name?.toLowerCase().trim());
        return found ? found.post_id : null;
    }

    resolveCategory = (name) => {
        const cats = (this.props.CATEGORIES && this.props.CATEGORIES.CHILD) || {};
        const found = Object.values(cats).find(c => c.category_name?.toLowerCase().trim() === name?.toLowerCase().trim());
        return found ? found.category_id : null;
    }

    resolveBank = (name) => {
        const banks = this.props.BANKS || {};
        const found = Object.values(banks).find(b => b.bank_name?.toLowerCase().trim() === name?.toLowerCase().trim());
        return found ? found.bank_id : null;
    }

    resolveProject = (name) => {
        if (!name || !name.trim()) return null;
        const projects = this.props.PROJECTS || {};
        const found = Object.values(projects).find(p => p.project_name?.toLowerCase().trim() === name?.toLowerCase().trim());
        return found ? found.project_id : null;
    }

    resolveChantier = (name) => {
        if (!name || !name.trim()) return null;
        const chantiers = this.props.CHANTIERS || {};
        const found = Object.values(chantiers).find(c => c.chantier_name?.toLowerCase().trim() === name?.toLowerCase().trim());
        return found ? found.chantier_id : null;
    }

    // ---- Template Download ----

    downloadCSVTemplate = () => {
        const headers = "Date;Poste;Catégorie;Montant;Référence;Description;Mode de Paiement;Banque;Projet;Chantier";
        const blob = new Blob(["\uFEFF" + headers], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "template_import_recettes.csv";
        link.click();
        URL.revokeObjectURL(url);
    }

    // ---- CSV Parsing ----

    handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            let text = evt.target.result;
            // Strip BOM if present
            if (text.charCodeAt(0) === 0xFEFF) {
                text = text.slice(1);
            }
            this.parseCSV(text);
        };
        reader.readAsText(file, "UTF-8");
    }

    parseCSV = (text) => {
        const lines = text.split(/\r?\n/).filter(l => l.trim() !== "");
        if (lines.length < 2) {
            this.setState({ errors: [{ row: "-", field: "fichier", message: "Le fichier est vide ou ne contient pas de données." }] });
            return;
        }

        // Skip header line
        const dataLines = lines.slice(1);

        if (dataLines.length > 100) {
            this.setState({ errors: [{ row: "-", field: "fichier", message: "Le fichier contient plus de 100 lignes de données (maximum autorisé: 100)." }] });
            return;
        }

        const errors = [];
        const parsedRows = [];

        dataLines.forEach((line, idx) => {
            const rowNum = idx + 2; // 1-based, row 1 is header
            const cols = line.split(";");

            if (cols.length < 8 || cols.length > 10) {
                errors.push({ row: rowNum, field: "Structure", message: `La ligne a ${cols.length} colonnes, entre 8 et 10 attendues.` });
                return;
            }

            const trimmed = cols.map(c => c.trim());
            const raw_date = trimmed[0] || "";
            const raw_post = trimmed[1] || "";
            const raw_category = trimmed[2] || "";
            const raw_amount = trimmed[3] || "";
            const raw_ref = trimmed[4] || "";
            const raw_desc = trimmed[5] || "";
            const raw_payment_mode = trimmed[6] || "";
            const raw_bank = trimmed[7] || "";
            const raw_project = trimmed[8] || "";
            const raw_chantier = trimmed[9] || "";

            const rowErrors = [];

            // Date validation DD/MM/YYYY
            let parsedDate = null;
            const dateParts = raw_date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (!dateParts) {
                rowErrors.push({ row: rowNum, field: "Date", message: `Date invalide "${raw_date}". Format attendu: JJ/MM/AAAA` });
            } else {
                const day = parseInt(dateParts[1], 10);
                const month = parseInt(dateParts[2], 10);
                const year = parseInt(dateParts[3], 10);
                const d = new Date(year, month - 1, day);
                if (isNaN(d.getTime()) || d.getDate() !== day || d.getMonth() !== month - 1 || d.getFullYear() !== year) {
                    rowErrors.push({ row: rowNum, field: "Date", message: `Date invalide "${raw_date}".` });
                } else {
                    parsedDate = d.toISOString();
                }
            }

            // Post
            const postId = this.resolvePost(raw_post);
            if (!postId) {
                rowErrors.push({ row: rowNum, field: "Poste", message: `Poste introuvable: "${raw_post}"` });
            }

            // Category
            const categoryId = this.resolveCategory(raw_category);
            if (!categoryId) {
                rowErrors.push({ row: rowNum, field: "Catégorie", message: `Catégorie introuvable: "${raw_category}"` });
            }

            // Amount
            const amount = parseFloat(raw_amount.replace(",", "."));
            if (isNaN(amount)) {
                rowErrors.push({ row: rowNum, field: "Montant", message: `Montant invalide: "${raw_amount}"` });
            }

            // Ref
            if (!raw_ref) {
                rowErrors.push({ row: rowNum, field: "Référence", message: "La référence est obligatoire." });
            }

            // Desc
            if (!raw_desc) {
                rowErrors.push({ row: rowNum, field: "Description", message: "La description est obligatoire." });
            }

            // Payment mode
            if (!VALID_PAYMENT_MODES.includes(raw_payment_mode.toUpperCase())) {
                rowErrors.push({ row: rowNum, field: "Mode de Paiement", message: `Mode de paiement invalide: "${raw_payment_mode}". Valeurs: ${VALID_PAYMENT_MODES.join(", ")}` });
            }

            // Bank
            const bankId = this.resolveBank(raw_bank);
            if (!bankId) {
                rowErrors.push({ row: rowNum, field: "Banque", message: `Banque introuvable: "${raw_bank}"` });
            }

            if (rowErrors.length > 0) {
                errors.push(...rowErrors);
            } else {
                // Optional fields
                const projectId = this.resolveProject(raw_project);
                const chantierId = this.resolveChantier(raw_chantier);

                parsedRows.push({
                    raw: { raw_date, raw_post, raw_category, raw_amount, raw_ref, raw_desc, raw_payment_mode, raw_bank, raw_project, raw_chantier },
                    resolved: {
                        date: parsedDate,
                        postId,
                        postName: raw_post,
                        categoryId,
                        categoryName: raw_category,
                        amount,
                        ref: raw_ref,
                        desc: raw_desc,
                        paymentMode: raw_payment_mode.toUpperCase(),
                        bankId,
                        bankName: raw_bank,
                        projectId: projectId || "",
                        chantierId: chantierId || "",
                    },
                    files: [],
                });
            }
        });

        if (errors.length > 0) {
            this.setState({ errors, parsedRows: [], phase: "PARSE" });
        } else {
            this.setState({ errors: [], parsedRows, phase: "ATTACH" });
        }
    }

    // ---- File attachment per row ----

    handleRowFileDrop = (rowIndex, acceptedFiles) => {
        const oversized = acceptedFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversized.length > 0) {
            this.props.notif && this.props.notif("error", `Fichier trop volumineux (max 5MB): ${oversized.map(f => f.name).join(", ")}`);
            return;
        }
        this.setState(prev => {
            const parsedRows = [...prev.parsedRows];
            parsedRows[rowIndex] = {
                ...parsedRows[rowIndex],
                files: [...parsedRows[rowIndex].files, ...acceptedFiles],
            };
            return { parsedRows };
        });
    }

    handleRemoveFile = (rowIndex, fileIndex) => {
        this.setState(prev => {
            const parsedRows = [...prev.parsedRows];
            const files = [...parsedRows[rowIndex].files];
            files.splice(fileIndex, 1);
            parsedRows[rowIndex] = { ...parsedRows[rowIndex], files };
            return { parsedRows };
        });
    }

    allRowsHaveFiles = () => {
        return this.state.parsedRows.length > 0 && this.state.parsedRows.every(r => r.files.length > 0);
    }

    // ---- Base64 encoding ----

    fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
            filename: file.name,
            data_uri: reader.result,
            filetype: file.type
        });
        reader.onerror = reject;
        reader.readAsDataURL(file);
    })

    // ---- Submit ----

    handleSubmit = async () => {
        const { parsedRows } = this.state;
        this.setState({ phase: "SUBMIT", submitProgress: 0, submitTotal: parsedRows.length, submitError: null, createdIds: [] });

        try {
            // Encode all files to base64
            const incomes = await Promise.all(parsedRows.map(async (row) => {
                const encodedFiles = await Promise.all(row.files.map(f => this.fileToBase64(f)));
                return {
                    income_date: row.resolved.date,
                    income_post: row.resolved.postId,
                    income_category: row.resolved.categoryId,
                    income_amount: row.resolved.amount,
                    income_ref: row.resolved.ref,
                    income_desc: row.resolved.desc,
                    income_payment_mode: row.resolved.paymentMode,
                    income_bank: row.resolved.bankId,
                    income_project: row.resolved.projectId || "",
                    income_chantier: row.resolved.chantierId || "",
                    income_files: encodedFiles,
                };
            }));

            const result = await this.props.importIncomes(incomes);

            if (result) {
                const createdIds = result.created_ids || [];
                this.setState({ phase: "REFRESHING", createdIds });
                try {
                    if (this.props.fetchIncomes) {
                        await this.props.fetchIncomes();
                    }
                } catch (e) {
                    console.log("[WARN] fetchIncomes after import failed", e);
                }
                this.props.notif && this.props.notif("success", `${createdIds.length} recette(s) importée(s) avec succès.`);
                this.props.onClose();
            } else {
                this.setState({ phase: "ERROR", submitError: "L'import a échoué. Aucune réponse du serveur." });
            }
        } catch (err) {
            const errorMsg = err?.response?.data?.message || err?.message || "Erreur lors de l'import.";
            this.setState({ phase: "ERROR", submitError: errorMsg });
        }
    }

    handleRollback = async () => {
        const { createdIds } = this.state;
        this.setState({ rollingBack: true });
        for (const id of createdIds) {
            try {
                await this.props.deleteIncome({ income_id: id });
            } catch (e) {
                console.log("[ERROR] rollback delete income", id, e);
            }
        }
        this.setState({ rollingBack: false });
        this.props.onClose();
    }

    handleReset = () => {
        this.setState({
            phase: "PARSE",
            parsedRows: [],
            errors: [],
            submitProgress: 0,
            submitTotal: 0,
            submitError: null,
            createdIds: [],
            rollingBack: false,
        });
    }

    // ---- Render helpers ----

    renderParsePhase() {
        const { errors } = this.state;
        return (
            <Box>
                <Typography variant="body1" gutterBottom>
                    Sélectionnez un fichier CSV à importer. Le séparateur doit être <strong>;</strong> (point-virgule).
                </Typography>
                <Box my={2} display="flex" alignItems="center" style={{ gap: 12 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        component="label"
                        startIcon={<CloudUploadRounded />}
                    >
                        Choisir un fichier CSV
                        <input
                            type="file"
                            accept=".csv"
                            hidden
                            onChange={this.handleFileSelect}
                        />
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.downloadCSVTemplate}
                        startIcon={<GetAppRounded />}
                    >
                        Télécharger le template
                    </Button>
                </Box>
                {errors.length > 0 && (
                    <Box mt={2}>
                        <Alert severity="error" style={{ marginBottom: 8 }}>
                            Le fichier contient {errors.length} erreur(s). Corrigez le CSV et réessayez.
                        </Alert>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ligne</TableCell>
                                    <TableCell>Champ</TableCell>
                                    <TableCell>Erreur</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {errors.map((e, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{e.row}</TableCell>
                                        <TableCell>{e.field}</TableCell>
                                        <TableCell>{e.message}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>
        );
    }

    renderAttachPhase() {
        const { parsedRows } = this.state;
        return (
            <Box>
                <Alert severity="info" style={{ marginBottom: 12 }}>
                    {parsedRows.length} ligne(s) prête(s) à importer. Joignez au moins un fichier par ligne avant de continuer.
                </Alert>
                <Box style={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Poste</TableCell>
                                <TableCell>Catégorie</TableCell>
                                <TableCell>Montant</TableCell>
                                <TableCell>Réf.</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Mode</TableCell>
                                <TableCell>Banque</TableCell>
                                <TableCell>Fichiers</TableCell>
                                <TableCell>Statut</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {parsedRows.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell style={{ whiteSpace: "nowrap" }}>
                                        {new Date(row.resolved.date).toLocaleDateString("fr-FR")}
                                    </TableCell>
                                    <TableCell>{row.resolved.postName}</TableCell>
                                    <TableCell>{row.resolved.categoryName}</TableCell>
                                    <TableCell>{row.resolved.amount}</TableCell>
                                    <TableCell>{row.resolved.ref}</TableCell>
                                    <TableCell>{row.resolved.desc}</TableCell>
                                    <TableCell>{row.resolved.paymentMode}</TableCell>
                                    <TableCell>{row.resolved.bankName}</TableCell>
                                    <TableCell style={{ minWidth: 180 }}>
                                        <Dropzone onDrop={(files) => this.handleRowFileDrop(idx, files)}>
                                            {({ getRootProps, getInputProps }) => (
                                                <div
                                                    {...getRootProps()}
                                                    style={{
                                                        border: "1px dashed #aaa",
                                                        borderRadius: 6,
                                                        padding: "4px 8px",
                                                        cursor: "pointer",
                                                        backgroundColor: "#fafafa",
                                                        fontSize: 12,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <Typography variant="caption" color="textSecondary">
                                                        Déposer ou cliquer
                                                    </Typography>
                                                </div>
                                            )}
                                        </Dropzone>
                                        {row.files.map((f, fi) => (
                                            <Chip
                                                key={fi}
                                                label={f.name}
                                                size="small"
                                                onDelete={() => this.handleRemoveFile(idx, fi)}
                                                deleteIcon={<DeleteForever />}
                                                style={{ margin: "2px 2px 0 0", maxWidth: 160 }}
                                            />
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        {row.files.length > 0
                                            ? <CheckCircle style={{ color: "#4caf50" }} fontSize="small" />
                                            : <Warning style={{ color: "#ff9800" }} fontSize="small" />
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        );
    }

    renderSubmitPhase() {
        const { submitTotal } = this.state;
        return (
            <Box p={2} textAlign="center">
                <Typography variant="body1" gutterBottom>
                    Import en cours... ({submitTotal} recette(s))
                </Typography>
                <LinearProgress style={{ marginTop: 16 }} />
            </Box>
        );
    }

    renderDonePhase() {
        const { createdIds } = this.state;
        return (
            <Box p={2} textAlign="center">
                <CheckCircle style={{ color: "#4caf50", fontSize: 48 }} />
                <Typography variant="h6" gutterBottom>
                    Import réussi !
                </Typography>
                <Typography variant="body2">
                    {createdIds.length} recette(s) créée(s).
                </Typography>
            </Box>
        );
    }

    renderRefreshingPhase() {
        const { createdIds } = this.state;
        return (
            <Box p={2} textAlign="center">
                <CheckCircle style={{ color: "#4caf50", fontSize: 48 }} />
                <Typography variant="h6" gutterBottom>
                    Import réussi !
                </Typography>
                <Typography variant="body2" gutterBottom>
                    {createdIds.length} recette(s) créée(s). Actualisation de la liste...
                </Typography>
                <LinearProgress style={{ marginTop: 16 }} />
            </Box>
        );
    }

    renderErrorPhase() {
        const { submitError, createdIds, rollingBack } = this.state;
        return (
            <Box p={2}>
                <Alert severity="error" style={{ marginBottom: 16 }}>
                    {submitError}
                </Alert>
                {createdIds.length > 0 && (
                    <Typography variant="body2" gutterBottom>
                        {createdIds.length} recette(s) ont déjà été créées avant l'erreur.
                    </Typography>
                )}
            </Box>
        );
    }

    render() {
        const { open, onClose } = this.props;
        const { phase, parsedRows, rollingBack, createdIds } = this.state;

        const title = {
            PARSE: "Importer des Recettes (CSV)",
            ATTACH: "Joindre les Fichiers",
            SUBMIT: "Import en cours...",
            REFRESHING: "Actualisation...",
            DONE: "Import Terminé",
            ERROR: "Erreur lors de l'Import",
        }[phase] || "Importer des Recettes";

        const isLocked = phase === "SUBMIT" || phase === "REFRESHING";

        return (
            <Dialog
                open={open}
                onClose={isLocked ? undefined : onClose}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        {title}
                        {!isLocked && (
                            <IconButton size="small" onClick={onClose}>
                                <Close />
                            </IconButton>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {phase === "PARSE" && this.renderParsePhase()}
                    {phase === "ATTACH" && this.renderAttachPhase()}
                    {phase === "SUBMIT" && this.renderSubmitPhase()}
                    {phase === "REFRESHING" && this.renderRefreshingPhase()}
                    {phase === "DONE" && this.renderDonePhase()}
                    {phase === "ERROR" && this.renderErrorPhase()}
                </DialogContent>
                <DialogActions>
                    {phase === "PARSE" && (
                        <Button onClick={onClose} color="inherit">
                            Annuler
                        </Button>
                    )}
                    {phase === "ATTACH" && (
                        <>
                            <Button onClick={this.handleReset} color="inherit">
                                Retour
                            </Button>
                            <Button onClick={onClose} color="inherit">
                                Annuler
                            </Button>
                            <Button
                                onClick={this.handleSubmit}
                                variant="contained"
                                color="primary"
                                disabled={!this.allRowsHaveFiles()}
                                startIcon={<CloudUploadRounded />}
                            >
                                Tout Importer ({parsedRows.length})
                            </Button>
                        </>
                    )}
                    {phase === "ERROR" && (
                        <>
                            <Button onClick={onClose} color="inherit">
                                Garder les recettes créées
                            </Button>
                            {createdIds.length > 0 && (
                                <Button
                                    onClick={this.handleRollback}
                                    color="secondary"
                                    variant="contained"
                                    disabled={rollingBack}
                                    startIcon={<DeleteForever />}
                                >
                                    {rollingBack ? "Annulation en cours..." : "Annuler tout"}
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>
        );
    }
}

export default ImportIncomesCSV;
